import type { ReactNode } from 'react';

import type { SessionUser } from '../../api/monitor';
import { Card } from '../../components/ui/Card';
import { Avatar } from '../../components/layout/UserBadge';
import type { ShellUser } from '../../components/layout/UserBadge';
import { hourMinute } from '../../lib/datetime';

interface ProfileScreenProps {
  /** Quem o shell está mostrando: vem da sessão do Google. */
  user: ShellUser;
  /** A mesma pessoa como o SICOP a guardou; nulo enquanto o cadastro não volta. */
  sessionUser: SessionUser | null;
  /** Ausente quando o app está rodando com o login desligado. */
  onSignOut?: () => void;
}

/** `16/08/2026 21:04` — o instante por extenso, que é como se lê um acesso. */
function timestamp(iso: string): string {
  const date = new Date(iso);

  return `${date.toLocaleDateString('pt-BR')} ${hourMinute(date)}`;
}

interface FieldProps {
  label: string;
  value: string;
  /** Pílula à direita dizendo de onde o valor vem. */
  source?: string;
  hint?: ReactNode;
}

/**
 * Um dado da conta, sempre em leitura.
 *
 * Parece um campo de formulário e não é um: nada nesta tela é editável, e a
 * pílula à direita diz por quê. Um `<input disabled>` daria o mesmo desenho com
 * a promessa falsa de que existe um estado em que ele abre.
 */
function Field({ label, value, source, hint }: FieldProps) {
  return (
    <div className="mt-3 first:mt-0">
      <div className="text-ink-600 text-[12px] font-bold">{label}</div>

      <div className="border-surface-300 bg-surface-100 text-ink-400 mt-1.5 flex items-center gap-2 rounded-[10px] border px-3 py-2.5 text-[14px] font-semibold">
        <span className="min-w-0 truncate">{value}</span>
        {source ? (
          <span className="bg-surface-200 ml-auto shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-bold">
            {source}
          </span>
        ) : null}
      </div>

      {hint ? <div className="text-ink-400 mt-1.5 text-[11.5px]">{hint}</div> : null}
    </div>
  );
}

/**
 * Tela de Perfil: quem está usando o SICOP, e de onde cada dado dessa
 * identidade vem.
 *
 * Não tem nada para salvar, e é de propósito. A conta é a do Google: nome, foto
 * e e-mail se trocam lá e chegam aqui no login seguinte — o SICOP não guarda
 * senha, então não há o que alterar. O único dado que é *nosso* é o papel de
 * acesso, e ele não se edita pelo próprio perfil.
 *
 * Sobra a leitura, que é o que se procura numa tela dessas: confirmar com qual
 * conta se está dentro (a pessoa tem duas no Google e entrou com a errada) e
 * sair dela.
 */
export function ProfileScreen({ user, sessionUser, onSignOut }: ProfileScreenProps) {
  // A foto da sessão é a mais nova; a do banco cobre o caso de a sessão do
  // Google não estar ligada e a pessoa já ter entrado alguma vez.
  const picture = user.picture ?? sessionUser?.pictureUrl ?? null;

  // Sem e-mail não há conta: é o visitante que o shell mostra enquanto o login
  // está desligado. A tela precisa dizer isso — os campos abaixo continuam
  // preenchidos, e sem o aviso pareceriam dados de alguém.
  const signedIn = Boolean(user.email);

  return (
    <>
      {signedIn ? null : (
        <div className="border-warn-200 bg-warn-100 text-warn-700 mb-3 rounded-xl border px-4 py-3 text-[12.5px] font-semibold">
          O login com o Google está desligado neste ambiente, então não há conta ligada: o
          SICOP mostra um visitante, e o que for feito entra na auditoria sem autor.
        </div>
      )}

      <Card className="flex items-center gap-4 p-5">
        <Avatar name={user.name} picture={picture} size={64} />

        <div className="min-w-0 flex-1">
          <div className="truncate text-[16px] font-extrabold">{user.name}</div>
          {user.email ? (
            <div className="text-ink-600 mt-0.5 truncate text-[12.5px]">{user.email}</div>
          ) : null}
          <div className="bg-surface-200 text-navy-800 mt-1.5 inline-block rounded-full px-2.5 py-0.5 text-[10.5px] font-bold">
            {user.role}
          </div>
        </div>

        {signedIn ? (
          <div className="text-ink-400 desktop:block hidden max-w-[168px] text-right text-[11.5px]">
            A foto vem da conta Google e é atualizada no login.
          </div>
        ) : null}
      </Card>

      <Card className="mt-3 p-5">
        <div className="mb-3 text-[14px] font-extrabold">Dados da conta</div>

        <Field
          label="Nome"
          value={user.name}
          source={signedIn ? 'conta Google' : undefined}
          hint="Sem nome na conta Google, o SICOP mostra o e-mail no lugar."
        />

        <Field
          label="E-mail"
          value={user.email || '—'}
          source={signedIn ? 'conta Google' : undefined}
          hint="O e-mail não pode ser alterado — o acesso é feito exclusivamente via conta Google, e o SICOP não guarda senha."
        />

        <Field
          label="Perfil de acesso"
          value={user.role}
          source="SICOP"
          hint="É o único dado desta tela que não vem do Google: quem administra o SICOP é decisão daqui, e só um administrador pode mudá-lo."
        />

        <Field
          label="Esta entrada"
          value={sessionUser?.lastLoginAt ? timestamp(sessionUser.lastLoginAt) : '—'}
          hint="Momento em que o login atual foi registrado. O histórico completo de entradas fica na Auditoria."
        />
      </Card>

      {onSignOut ? (
        <button
          type="button"
          onClick={onSignOut}
          className="border-danger-200 text-danger-600 desktop:hidden mt-3 w-full rounded-xl border bg-white p-3.5 text-[14px] font-extrabold"
        >
          Sair da conta
        </button>
      ) : null}
    </>
  );
}
